/**
 * Fatigue Tab
 *
 * Displays per-ad fatigue scoring and AI analysis.
 * Reports are cached in DynamoDB. Shows a "Regenerate" button to re-trigger analysis.
 */

import { useState, useEffect, useCallback } from 'react'
import * as Icons from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { analysisApi } from './api'
import type { FatigueReportResponse, FatigueClassification } from './types'

// ============================================================================
// Helpers
// ============================================================================

function getFatigueColor(label: string): string {
  switch (label) {
    case 'Healthy': return 'text-tf-feedback-success-text'
    case 'Moderate': return 'text-tf-feedback-warning-text'
    case 'Fatigued': return 'text-tf-feedback-warning-text'
    case 'Critical': return 'text-tf-feedback-error-text'
    default: return 'text-tf-text-muted'
  }
}

function getFatigueDotColor(label: string): string {
  switch (label) {
    case 'Healthy': return 'bg-tf-success'
    case 'Moderate': return 'bg-tf-warning'
    case 'Fatigued': return 'bg-orange-500'
    case 'Critical': return 'bg-tf-critical'
    default: return 'bg-tf-text-muted'
  }
}

function getFatigueBgColor(label: string): string {
  switch (label) {
    case 'Healthy': return 'border-tf-feedback-success-border bg-tf-feedback-success-bg/50'
    case 'Moderate': return 'border-tf-feedback-warning-border bg-tf-feedback-warning-bg/50'
    case 'Fatigued': return 'border-tf-feedback-warning-border bg-tf-feedback-warning-bg/50'
    case 'Critical': return 'border-tf-feedback-error-border bg-tf-feedback-error-bg/50'
    default: return 'border-tf-border'
  }
}

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toFixed(2)}`
}

// ============================================================================
// Ad Fatigue Card
// ============================================================================

function FatigueCard({ ad }: { ad: FatigueClassification }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-3 ${getFatigueBgColor(ad.fatigue_label)}`}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className={`w-3 h-3 rounded-full flex-shrink-0 ${getFatigueDotColor(ad.fatigue_label)}`} />
        <span className={`text-sm font-semibold ${getFatigueColor(ad.fatigue_label)}`}>
          {ad.fatigue_label}
        </span>
        <span className="text-xs text-tf-text-muted ml-1">
          Score: {ad.fatigue_score.toFixed(1)}
        </span>
      </div>

      {/* Ad name */}
      <p className="text-sm font-medium text-tf-text truncate" title={ad.ad_name}>
        {ad.ad_name}
      </p>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <span className="text-tf-text-muted block">Spend</span>
          <span className="font-medium text-tf-text">{formatCurrency(ad.spend)}</span>
        </div>
        <div>
          <span className="text-tf-text-muted block">ROAS</span>
          <span className="font-medium text-tf-text">{ad.roas.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-tf-text-muted block">% of Total</span>
          <span className="font-medium text-tf-text">{ad.percent_of_spend.toFixed(1)}%</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <span className="text-tf-text-muted block">CTR</span>
          <span className="font-medium text-tf-text">{ad.ctr.toFixed(2)}%</span>
        </div>
        <div>
          <span className="text-tf-text-muted block">CPM</span>
          <span className="font-medium text-tf-text">${ad.cpm.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-tf-text-muted block">Days</span>
          <span className="font-medium text-tf-text">{ad.lifetime_days}</span>
        </div>
      </div>

      {/* Budget Pressure bar */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-tf-text-muted">Budget Pressure:</span>
        <div className="flex-1 h-1.5 bg-tf-bg-hover rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${
              ad.budget_pressure_score >= 7 ? 'bg-tf-critical' : ad.budget_pressure_score >= 5 ? 'bg-tf-warning' : 'bg-tf-success'
            }`}
            style={{ width: `${ad.budget_pressure_score * 10}%` }}
          />
        </div>
        <span className="text-xs font-medium text-tf-text-sub">{ad.budget_pressure_score.toFixed(1)}</span>
      </div>

      {/* AI Analysis (expandable) */}
      {ad.analysis && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary-dark transition-colors self-start"
          >
            <Icons.Sparkles className="w-3.5 h-3.5" />
            {expanded ? 'Hide Analysis' : 'View AI Analysis'}
            {expanded ? <Icons.ChevronUp className="w-3 h-3" /> : <Icons.ChevronDown className="w-3 h-3" />}
          </button>
          {expanded && (
            <div className="bg-tf-bg-card rounded-lg border border-tf-border p-3 prose prose-xs prose-gray max-w-none">
              <ReactMarkdown>{ad.analysis}</ReactMarkdown>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

interface FatigueTabProps {
  startDate: string
  endDate: string
}

export function FatigueTab({ startDate, endDate }: FatigueTabProps) {
  const [data, setData] = useState<FatigueReportResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchReport = useCallback(async (regenerate = false) => {
    if (regenerate) {
      setRegenerating(true)
    } else {
      setLoading(true)
    }
    setError(null)
    try {
      const report = await analysisApi.getFatigueReport(startDate, endDate, regenerate)
      setData(report)
    } catch (err) {
      console.error('Error fetching fatigue report:', err)
      setError('Failed to load fatigue report')
    } finally {
      setLoading(false)
      setRegenerating(false)
    }
  }, [startDate, endDate])

  useEffect(() => {
    fetchReport(false)
  }, [fetchReport])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Icons.Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-3 text-sm text-tf-text-muted">Analyzing creative fatigue...</span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Icons.AlertCircle className="w-8 h-8 text-tf-feedback-error-text" />
        <p className="text-sm text-tf-feedback-error-text">{error || 'No data available'}</p>
        <button
          onClick={() => fetchReport(false)}
          className="mt-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Cache status + Regenerate */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-tf-text-muted">
          {data.from_cache ? (
            <>
              <Icons.Database className="w-3.5 h-3.5" />
              <span>Cached analysis from {new Date(data.generated_at).toLocaleString()}</span>
            </>
          ) : (
            <>
              <Icons.Sparkles className="w-3.5 h-3.5 text-primary" />
              <span>Fresh analysis generated {new Date(data.generated_at).toLocaleString()}</span>
            </>
          )}
        </div>
        <button
          onClick={() => fetchReport(true)}
          disabled={regenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/30 rounded-lg hover:bg-primary/15 transition-colors disabled:opacity-50"
        >
          {regenerating ? (
            <>
              <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />
              Re-analyzing...
            </>
          ) : (
            <>
              <Icons.RefreshCw className="w-3.5 h-3.5" />
              Regenerate Analysis
            </>
          )}
        </button>
      </div>

      {/* Overall Score Header */}
      <div className="flex items-center gap-4 p-4 bg-tf-bg-card rounded-xl border border-tf-border">
        <span className="text-lg font-semibold text-tf-text">Creative Fatigue Score:</span>
        <div className="flex items-center gap-2">
          <span className={`w-4 h-4 rounded-full ${getFatigueDotColor(data.overall_label)}`} />
          <span className={`text-lg font-bold ${getFatigueColor(data.overall_label)}`}>
            {data.overall_score.toFixed(1)} — {data.overall_label}
          </span>
        </div>
      </div>

      {/* AI Summary */}
      {data.summary && (
        <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
          <h3 className="text-sm font-semibold text-tf-text mb-2">Analysis Summary</h3>
          <div className="prose prose-sm prose-gray max-w-none">
            <ReactMarkdown>{data.summary}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Ad Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
        {data.ads.map((ad) => (
          <FatigueCard key={ad.ad_id} ad={ad} />
        ))}
      </div>

      {data.ads.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Icons.CheckCircle2 className="w-8 h-8 text-tf-feedback-success-text" />
          <p className="text-sm text-tf-text-muted">No campaigns found in the selected date range.</p>
        </div>
      )}
    </div>
  )
}
